import { animate, style, transition, trigger } from '@angular/animations';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map, Subscription } from 'rxjs';

import { twJoin, twMerge } from '../../../../utils';
import { LayoutFeature, LayoutNavigationItem } from '../../layout.model';
import { isItemActive } from '../../layout.utils';
import { IdGeneratorService } from '@lib/fresco';
import { NavIconComponent } from './nav-icon.component';
import { NgIf, NgFor, NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '../../../../icons/icon.component';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'da-nav-popover',
    template: `
    <div
      [class]="
        twMerge(
          'tw-relative tw-flex tw-w-full tw-items-center tw-py-0 tw-text-left tw-text-xs rtl:tw-text-right',
          'hover:tw-bg-[rgb(from_var(--apollo-layout-highlight)_r_g_b_/_15%)] hover:tw-text-[var(--apollo-layout-highlight)]',
          'focus:tw-bg-[rgb(from_var(--apollo-layout-highlight)_r_g_b_/_15%)] focus:tw-text-[var(--apollo-layout-highlight)] focus:tw-outline-none',
          'active:tw-bg-[rgb(from_var(--apollo-layout-highlight)_r_g_b_/_30%)] active:tw-text-[var(--apollo-layout-highlight)]',
          isNavigationScrolling && 'tw-pl-3 tw-pr-3 rtl:tw-pl-3 rtl:tw-pr-3',
          !isNavigationScrolling && 'tw-pl-7 tw-pr-3 rtl:tw-pl-3 rtl:tw-pr-7',
          active
            ? 'tw-font-semibold tw-text-[var(--apollo-layout-highlight)]'
            : '',
          open
            ? 'tw-bg-[rgb(from_var(--apollo-layout-highlight)_r_g_b_/_15%)]'
            : '',
          previewHover
            ? 'tw-bg-[rgb(from_var(--apollo-layout-highlight)_r_g_b_/_15%)] tw-text-[var(--apollo-layout-highlight)]'
            : ''
        )
      "
      (focusout)="handleTriggerFocusOut($event)"
      (mouseenter)="handleTriggerHover(true, $event)"
      (mouseleave)="handleTriggerHover(false, $event)"
    >
      <ng-container *ngIf="item.routerLink; else popoverExternalLink">
        <a
          [routerLink]="item.routerLink"
          class="tw-flex tw-flex-1 tw-items-center tw-py-3"
        >
          <ng-container *ngTemplateOutlet="popoverLinkContent"></ng-container>
        </a>
      </ng-container>

      <ng-template #popoverExternalLink>
        <a
          [href]="item.href"
          [attr.target]="item.target"
          class="tw-flex tw-flex-1 tw-items-center tw-py-3"
        >
          <ng-container *ngTemplateOutlet="popoverLinkContent"></ng-container>
        </a>
      </ng-template>

      <ng-template #popoverLinkContent>
        <da-nav-icon
          [item]="item"
          [active]="active"
          [className]="
            twJoin(
              'tw-size-6 tw-shrink-0 tw-min-w-6',
              iconOnly
                ? 'tw-mr-1 rtl:tw-mr-0 rtl:tw-ml-1'
                : 'tw-mr-2 rtl:tw-mr-0 rtl:tw-ml-2',
              item.rtlMirrorIcon ? 'rtl:tw-scale-x-[-1]' : ''
            )
          "
        />

        <span
          [class]="
            twJoin(
              iconOnly ? 'tw-sr-only' : 'tw-truncate',
              beta?.enabled && item.isBeta ? '' : 'tw-grow'
            )
          "
          >{{ item.text }}</span
        >

        <div
          *ngIf="beta?.enabled && item.isBeta"
          [class]="
            twJoin(
              'tw-grow',
              iconOnly
                ? 'tw-absolute tw-right-2 tw-top-1 rtl:tw-left-2 rtl:tw-right-auto'
                : 'tw--mt-2 tw-flex tw-self-start'
            )
          "
        >
          <div
            [class]="
              twJoin(
                'tw-rounded-2xl tw-border tw-border-purple-300 tw-bg-purple-100 tw-px-1 tw-py-0.5 tw-text-[8px] tw-font-extrabold tw-uppercase tw-leading-3 tw-text-purple-800'
              )
            "
          >
            {{ beta.text }}
          </div>
        </div>
      </ng-template>

      <button
        cdkOverlayOrigin
        #trigger="cdkOverlayOrigin"
        type="button"
        class="tw-flex tw-items-center tw-py-3"
        [title]="item.text"
        [attr.data-dgat]="item.dgat"
        [attr.hidden]="visibleChildren.length === 0"
        (click)="handleTriggerClick(item, $event)"
        [id]="toggleId"
        [attr.aria-controls]="popupId"
        [attr.aria-expanded]="!!open"
      >
        <da-icon
          icon="chevron-right"
          class="tw-p-[6px]"
          [className]="
            twJoin(
              'tw-size-3 rtl:tw-rotate-180',
              iconOnly ? '' : 'tw-ml-2 rtl:tw-ml-0 rtl:tw-mr-2'
            )
          "
        />
      </button>

      <div
        *ngIf="active"
        [class]="
          twJoin(
            'tw-absolute tw-inset-y-0 tw-right-0 tw-w-1 tw-rounded-l tw-bg-[var(--apollo-layout-highlight)] rtl:tw-left-0 rtl:tw-right-auto rtl:tw-rounded-l-none rtl:tw-rounded-r'
          )
        "
      ></div>
    </div>

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="open"
      [cdkConnectedOverlayPositions]="[
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]"
      [cdkConnectedOverlayHasBackdrop]="false"
      cdkConnectedOverlayPanelClass="tw-reset"
      (detach)="open = false"
      (overlayKeydown)="handlePopoverKeydown($event)"
    >
      <div
        #popover
        @PopoverTrigger
        cdkTrapFocus
        class="tw-pl-2 rtl:tw-pl-0 rtl:tw-pr-2"
        (mouseleave)="handlePopoverMouseLeave($event)"
      >
        <div
          class="tw-reset tw-min-w-60 tw-origin-left tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-py-2 tw-shadow-xl tw-transition rtl:tw-origin-right"
        >
          <header
            class="tw-border-b tw-border-neutral-300 tw-px-4 tw-pb-4 tw-pt-2"
          >
            <h4 class="tw-font-extrabold">{{ item.text }}</h4>
          </header>
          <ul
            class="tw-flex tw-flex-col"
            [id]="popupId"
            [attr.aria-labeledby]="toggleId"
          >
            <li
              *ngFor="let child of visibleChildren; let i = index"
              [class]="
                i > 0 && child?.includeSeparator
                  ? 'tw-border-t tw-border-neutral-300'
                  : ''
              "
            >
              <ng-container *ngIf="child?.routerLink; else externalLink">
                <a
                  [routerLink]="child.routerLink"
                  [class]="
                    twMerge(
                      'tw-flex tw-items-center tw-px-4 tw-py-2 hover:tw-bg-neutral-100 focus:tw-bg-neutral-100 focus-visible:tw-outline-none',
                      isItemActive(child) ? 'tw-font-semibold' : ''
                    )
                  "
                  [attr.data-dgat]="child.dgat"
                  [attr.aria-current]="isItemActive(child) ? 'page' : null"
                  (click)="handleClick(child)"
                >
                  <da-icon
                    *ngIf="child.icon"
                    [icon]="child.icon"
                    type="solid"
                    className="tw-mr-2 rtl:tw-mr-0 rtl:tw-ml-2 tw-size-6 tw-shrink-0"
                  />

                  <span class="tw-grow">{{ child.text }}</span>

                  <da-icon
                    *ngIf="isItemActive(child)"
                    icon="check-circle"
                    type="solid"
                    className="tw-ml-4 rtl:tw-ml-0 rtl:tw-mr-4 tw-text-green-600 tw-size-6 tw-shrink-0"
                  />
                </a>
              </ng-container>

              <ng-template #externalLink>
                <a
                  [href]="child.href"
                  [attr.target]="child.target"
                  [class]="
                    twMerge(
                      'tw-flex tw-items-center tw-px-4 tw-py-2 hover:tw-bg-neutral-100 focus:tw-bg-neutral-100 focus-visible:tw-outline-none',
                      isItemActive(child) ? 'tw-font-semibold' : ''
                    )
                  "
                  [attr.data-dgat]="child.dgat"
                  [attr.aria-current]="isItemActive(child) ? 'page' : null"
                  (click)="handleClick(child)"
                >
                  <da-icon
                    *ngIf="child.icon"
                    [icon]="child.icon"
                    type="solid"
                    className="tw-mr-2 rtl:tw-mr-0 rtl:tw-ml-2 tw-size-6 tw-shrink-0"
                  />

                  <span class="tw-grow">{{ child.text }}</span>

                  <da-icon
                    *ngIf="isItemActive(child)"
                    icon="check-circle"
                    type="solid"
                    className="tw-ml-4 rtl:tw-ml-0 rtl:tw-mr-4 tw-text-green-600 tw-size-6 tw-shrink-0"
                  />
                </a>
              </ng-template>
            </li>
          </ul>
        </div>
      </div>
    </ng-template>
  `,
    animations: [
        trigger('PopoverTrigger', [
            transition(':enter', [
                style({ opacity: 0, transform: 'scale(95%)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'scale(100%)' })),
            ]),
            transition(':leave', [
                style({ opacity: 1, transform: 'scale(100%)' }),
                animate('100ms ease-in', style({ opacity: 0, transform: 'scale(95%)' })),
            ]),
        ]),
    ],
    imports: [
        CdkOverlayOrigin,
        NavIconComponent,
        NgIf,
        IconComponent,
        CdkConnectedOverlay,
        CdkTrapFocus,
        NgFor,
        NgTemplateOutlet,
        RouterLink,
    ]
})
export class NavPopoverComponent implements OnInit, OnDestroy {
  @Input() public item!: LayoutNavigationItem;
  @Input() public iconOnly? = false;
  @Input() public beta?: LayoutFeature;
  @Input() public active = false;
  @Input() public previewHover = false;
  @Input() public scaled = false;
  @Input() public isNavigationScrolling = false;

  @ViewChild(CdkOverlayOrigin) public trigger!: CdkOverlayOrigin;
  @ViewChild('popover') public popover?: ElementRef;
  @ViewChild(CdkTrapFocus) public trapFocus?: CdkTrapFocus;

  public open = false;
  public twMerge = twMerge;
  public twJoin = twJoin;
  public toggleId: string;
  public popupId: string;

  private subscription: Subscription;

  public get visibleChildren() {
    const isChildVisible = (child) => child.visible;
    return this.item.children.filter(isChildVisible);
  }

  constructor(
    private router: Router,
    private idGenerator: IdGeneratorService,
    private cd: ChangeDetectorRef
  ) {
    this.watchPathname();
  }

  public isItemActive = (item: LayoutNavigationItem) => false;

  public ngOnInit() {
    this.toggleId = this.idGenerator.generateStringId('nav-menu-toggle');
    this.popupId = this.idGenerator.generateStringId('nav-popup-menu');
  }

  public ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  public handleTriggerHover(
    open: boolean,
    event: MouseEvent
  ) {
    if (open) {
      this.open = true;
    } else {
      // If the mouse is leaving the trigger, check if it's entering the popover
      // If so, keep the popover open, otherwise close it
      const popover = this.popover?.nativeElement;
      this.open =
        event.relatedTarget === popover ||
        popover?.contains(event.relatedTarget as Node);
    }
  }

  public handleTriggerFocusOut(event: FocusEvent) {
    // If the focus is leaving the trigger, check if it's entering the popover
    // If so, keep the popover open, otherwise close it
    const popover = this.popover?.nativeElement;
    this.open =
      event.relatedTarget === popover ||
      popover?.contains(event.relatedTarget as Node);
  }

  public handleTriggerClick(item: LayoutNavigationItem, event: MouseEvent) {
    item.trackEvent?.();

    this.open = !this.open;
    if (this.open) {
      // Wait for the popover to animate in before focusing the first tabbable element
      setTimeout(() => {
        this.trapFocus?.focusTrap?.focusFirstTabbableElementWhenReady();
      }, 250);
    }
  }

  public handlePopoverMouseLeave(event: MouseEvent) {
    // If the mouse is leaving the popover, check if it's entering the trigger
    // If so, keep the popover open, otherwise close it
    const trigger = this.trigger.elementRef.nativeElement;
    this.open =
      event.relatedTarget === trigger ||
      trigger.contains(event.relatedTarget as Node);
  }

  public handlePopoverKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      // Focus the trigger button when the popover is closed by pressing Escape
      const trigger = this.trigger.elementRef.nativeElement;
      trigger.focus();
    }
  }

  public handleClick(child: LayoutNavigationItem) {
    child.trackEvent?.();
    this.open = false;
  }

  /**
   * When route changes, update pathname and trigger re-renders to recalculate which item is active
   */
  private watchPathname() {
    this.isItemActive = isItemActive(this.router.url);

    this.subscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => (event as NavigationEnd).urlAfterRedirects)
      )
      .subscribe((url) => {
        this.isItemActive = isItemActive(url);
        this.cd.detectChanges();
      });
  }
}
